/**
 * Estimation Symphonics — écran repris de la version du client (itérations 19, 20, 29, 30, 32).
 *
 * Contenu, libellés et disposition sont les siens. Trois adaptations seulement :
 * nos chemins d'import, notre route de paramétrage au pluriel, et deux fonctions
 * d'adaptation de notre API vers la forme que ses composants attendent.
 *
 * Le calcul, lui, est le nôtre depuis le 04/08 : barème contractuel, discipline
 * d'exclusion, cloisonnement par sous-compte. Cet écran le rend enfin visible.
 */
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/shared/auth/AuthContext';
import useCapacites from '@/shared/auth/useCapacites';
import { CAP } from '@/shared/auth/capacites';
import { Navigate } from 'react-router-dom';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Card, CardContent } from '@/shared/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Badge } from '@/shared/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/ui/dialog';
import { Slider } from '@/shared/ui/slider';
import { Separator } from '@/shared/ui/separator';
import Breadcrumb from '@/shared/components/Breadcrumb';
import AggregatorDocuments from '@/shared/components/AggregatorDocuments';
import AggregatorAssetsTable from '@/shared/components/AggregatorAssetsTable';
import { SymphonicsRevenueWidget } from '@/shared/components/AggregatorRevenueWidgets';
import MentionEstimation from '@/shared/components/MentionEstimation';
import { toast } from 'sonner';
import {
  Save, Plus, Trash2, TrendingUp, TrendingDown, Euro, Flame, Droplets,
  Sun, BatteryCharging, HelpCircle, AlertCircle, Sliders, RotateCcw,
} from 'lucide-react';

// --- Adaptation de notre API vers la forme attendue par ses composants -----------
//
// Sa version rend une liste plate d'actifs et un résumé « à plat ». La nôtre rend
// `{retenus, ecartes}` et `{totaux, par_type, par_mecanisme}`, parce que c'est la
// forme qui sert le calcul. On convertit ici, en un seul endroit et sous un nom
// explicite, plutôt que de retoucher trois composants : le jour où on comparera de
// nouveau avec son dépôt, seule cette fonction sera à lire.

const adapterActifs = (charge) => {
  const ligne = (a, statut) => ({
    ...a,
    id: a.asset_id,
    aggregateur: 'Symphonics',
    statut,
    revenu: a.annuel ?? null,
    raison_exclusion: a.motif_libelle || null,
  });
  return [
    ...(charge?.retenus || []).map((a) => ligne(a, 'actif')),
    // « exclu » désigne un geste humain, « en attente » une cause contractuelle ou un
    // paramétrage manquant. Les deux ne se corrigent pas de la même façon, et l'écran
    // doit les distinguer.
    ...(charge?.ecartes || []).map((a) =>
      ligne(a, a.motif === 'exclu_manuellement' ? 'exclu' : 'en_attente')),
  ];
};

const adapterResume = (s, actifs) => {
  if (!s) return null;
  const parType = {};
  for (const [type, v] of Object.entries(s.par_type || {})) {
    parType[type] = {
      // `count` et non `actifs` : c'est le nom que lisent les cartes de ventilation.
      // Avec le mauvais nom elles affichaient « 0 actif » au-dessus d'un revenu non nul,
      // et se désactivaient elles-mêmes en croyant le type vide.
      count: v.actifs,
      actifs: v.actifs,
      annuel: v.annuel,
      mensuel: v.annuel != null ? v.annuel / 12 : null,
      puissance_kw: v.puissance_kw,
      // Ventilation par mécanisme, lue par le bandeau de contexte.
      mecapa_an: v.capacitaire,
      energie_an: v.energie,
      // Le widget signale les types en attente de paramétrage. Le résumé ne les
      // compte pas par type, on les recompte depuis la liste déjà chargée.
      en_attente: (actifs || []).filter(
        (a) => a.type === type && a.statut === 'en_attente').length,
    };
  }
  return {
    ...s,
    by_type: parType,
    total_mensuel: s.totaux?.mensuel,
    total_annuel: s.totaux?.annuel,
    total_mecapa_an: s.par_mecanisme?.capacitaire,
    total_energie_an: s.par_mecanisme?.energie,
    total_actifs_symphonics: s.totaux?.actifs_retenus,
    total_clients_symphonics: s.totaux?.clients,
    // Servis tels quels par notre résumé depuis le correctif du 10/08 : sans eux,
    // l'en-tête annonçait « 0 sous mandat » sur un parc qui en compte 20.
    total_actifs_operateur: s.total_actifs_operateur,
    ventilation: s.ventilation,
  };
};

const TYPE_ICON = { PAC: Flame, PV: Sun, Ballon: Droplets, ECS: Droplets, Batterie: BatteryCharging };
const TYPE_COLOR = { PAC: 'text-orange-500 bg-orange-50', PV: 'text-amber-500 bg-amber-50', Ballon: 'text-cyan-500 bg-cyan-50', ECS: 'text-cyan-500 bg-cyan-50', Batterie: 'text-violet-500 bg-violet-50' };
const TYPE_LABEL = { PAC: 'PAC', PV: 'PV', Ballon: 'Ballon ECS', ECS: 'Ballon ECS', Batterie: 'Batterie' };

// Nom générique de l'agrégateur de la page — permet de dupliquer facilement pour un autre agrégateur.
const PAGE_AGGREGATEUR = 'Symphonics';

const fmtEUR = (v) => (v == null ? '—' : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

function SymphoCOPCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [cop, setCop] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/admin/pac-cop').then(r => setCop(r.data.pac_cop)).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/pac-cop', { value: parseFloat(cop) });
      toast.success('COP PAC enregistré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };
  return (
    <Card data-testid="cop-pac-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <Flame className="w-4 h-4 text-orange-500" />
          <p className="text-sm font-semibold text-neutral-900">Coefficient de performance PAC (COP)</p>
        </div>
        <p className="text-[12px] text-neutral-500 mb-3">
          Utilisé pour convertir la puissance thermique d&apos;une pompe à chaleur en puissance électrique équivalente.
        </p>
        <div className="flex items-end gap-3 max-w-md">
          <div className="flex-1">
            <Label htmlFor="pac-cop" className="text-[11px] text-neutral-500">COP par défaut</Label>
            <Input id="pac-cop" type="number" step="0.1" min="1" max="10" value={cop}
              onChange={(e) => setCop(e.target.value)} disabled={!isAdmin}
              className="h-9 mt-1" data-testid="pac-cop-input" />
          </div>
          {isAdmin && (
            <Button onClick={save} disabled={saving} data-testid="pac-cop-save">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SymphonicsPage() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [versements, setVersements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [form, setForm] = useState({
    taux_mecapa: '', taux_energie: '', part_operateur: 1,
  });
  const [versementForm, setVersementForm] = useState({
    periode: '', date_versement: new Date().toISOString().slice(0, 10), montant_reel: '',
  });
  const [savingVersement, setSavingVersement] = useState(false);
  const [assetsList, setAssetsList] = useState([]);
  const [editingAsset, setEditingAsset] = useState(null); // { asset, form }
  const [savingAsset, setSavingAsset] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null); // null | 'PAC' | 'Ballon' | 'Batterie' | 'PV'

  const isAdmin = user?.role === 'admin';
  // LA GARDE LIT LA CAPACITE, plus une liste de roles ecrite ici (lot I, 30/08).
  //
  // Cette liste etait une copie de la matrice du serveur. Elle a deja coute deux fois :
  // le 25/08 parce qu'elle divergeait de celle de Survoltage -- la meme entree de menu
  // redirigeait ici et tournait dans le vide la -- puis le 30/08 parce que le
  // gestionnaire a perdu `tarifs:read` sans que la copie le sache : l'ecran s'ouvrait
  // encore et restait vide. Les deux ecrans sont jumeaux et lisent maintenant la meme
  // capacite, celle que leurs routes exigent reellement.
  //
  // Tant que la reponse n'est pas arrivee, on laisse passer plutot que de rediriger :
  // un renvoi a l'accueil au premier rendu ferait sortir de la page qu'on vient d'ouvrir.
  // L'ECRITURE reste a l'admin, par `isAdmin` sur chaque champ et par `tarifs:write`
  // au serveur.
  const { possede, pret } = useCapacites();
  const hasAccess = !pret || possede(CAP.TARIFS_READ);

  const fetchAll = async () => {
    try {
      const [cfg, sum, vers, ass] = await Promise.all([
        api.get('/symphonics/config'),
        api.get('/symphonics/revenue-summary'),
        api.get('/symphonics/versements'),
        api.get('/symphonics/assets'),
      ]);
      setConfig(cfg.data);
      // `/versements` rend `{versements, total_recu, total_attendu}` chez nous, un
      // tableau chez lui. Sans cette extraction, `versements.map` lève et l'écran
      // entier reste blanc : c'est la panne du 10/08, invisible à toute vérification
      // qui ne charge pas la page.
      setVersements(vers.data?.versements || []);
      // La liste d'abord : le résumé s'en sert pour compter les types en attente,
      // et l'état React n'est pas encore à jour à cet instant.
      const actifs = adapterActifs(ass.data);
      setAssetsList(actifs);
      setSummary(adapterResume(sum.data, actifs));
      setForm({
        taux_mecapa: cfg.data.taux_mecapa ?? '',
        taux_energie: cfg.data.taux_energie ?? '',
        part_operateur: cfg.data.part_operateur ?? 1,
      });
    } catch (e) {
      console.error(e);
      toast.error('Impossible de charger les données Symphonics');
    }
    setLoading(false);
  };

  useEffect(() => {
    // Ne pas déclencher les appels API pour les rôles non autorisés :
    // évite 3× HTTP 403 dans la console avant que <Navigate> ne redirige.
    if (!user) return;
    if (!hasAccess) { setLoading(false); return; }
    fetchAll();
  }, [user, hasAccess]);

  // Access control : rediriger vers la home role-specifique (backend enforce quand meme).
  if (user && !hasAccess) {
    const roleHome = user.role === 'installateur' ? '/installateur'
      : user.role === 'distributeur' ? '/sav'
      : '/dashboard';
    return <Navigate to={roleHome} replace />;
  }

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const payload = {};
      for (const k of ['taux_mecapa', 'taux_energie', 'part_operateur']) {
        const v = form[k];
        // Chaine vide -> null : « en attente de paramétrage », pas « zéro ».
        payload[k] = (v === '' || v === null) ? null : parseFloat(v);
      }
      const r = await api.put('/symphonics/config', payload);
      setConfig(r.data);
      toast.success('Paramètres Symphonics enregistrés');
      // Rafraichir le summary avec les nouvelles valeurs
      const sum = await api.get('/symphonics/revenue-summary');
      setSummary(adapterResume(sum.data, assetsList));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
    setSavingConfig(false);
  };

  /**
   * Enregistre les quatre paramètres d'UN type d'actif.
   *
   * Les quatre partent ensemble, toujours. Le serveur écrit
   * `types.<type>.<champ>` pour chacun des quatre champs à partir du modèle validé :
   * n'en envoyer qu'un remettrait les trois autres à null, donc effacerait le
   * paramétrage du type au lieu de le modifier.
   */
  const enregistrerType = async (type, valeurs) => {
    const params = {};
    for (const champ of ['coeff_mecapa', 'coeff_dispo', 'taux_mecapa', 'taux_energie']) {
      const v = valeurs[champ];
      params[champ] = (v === '' || v === null || v === undefined) ? null : parseFloat(v);
    }
    const r = await api.put('/symphonics/config', { types: { [type]: params } });
    setConfig(r.data);
    // Le revenu du parc change avec ces coefficients : le laisser à l'écran tel quel
    // afficherait un total qui ne correspond plus aux paramètres visibles.
    await fetchAll();
  };

  const addVersement = async (e) => {
    e.preventDefault();
    if (!versementForm.montant_reel || !versementForm.date_versement) {
      toast.error('Montant et date requis');
      return;
    }
    setSavingVersement(true);
    try {
      await api.post('/symphonics/versements', {
        periode: versementForm.periode || 'Sans période',
        date_versement: versementForm.date_versement,
        montant_reel: parseFloat(versementForm.montant_reel),
        estime_a_date: summary?.total_mensuel ?? 0,
      });
      toast.success('Versement ajouté');
      setVersementForm({ periode: '', date_versement: new Date().toISOString().slice(0, 10), montant_reel: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSavingVersement(false);
  };

  const deleteVersement = async (id) => {
    if (!window.confirm('Supprimer ce versement ?')) return;
    try {
      await api.delete(`/symphonics/versements/${id}`);
      toast.success('Versement supprimé');
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const openAssetParams = (asset) => {
    const p = asset.symphonics_params || {};
    setEditingAsset({
      asset,
      form: {
        coeff_mecapa: p.coeff_mecapa ?? '',
        coeff_dispo: p.coeff_dispo ?? '',
        taux_mecapa: p.taux_mecapa ?? '',
        taux_energie: p.taux_energie ?? '',
      },
    });
  };

  const saveAssetParams = async () => {
    if (!editingAsset) return;
    setSavingAsset(true);
    try {
      const body = {};
      for (const k of ['coeff_mecapa', 'coeff_dispo', 'taux_mecapa', 'taux_energie']) {
        const v = editingAsset.form[k];
        if (v !== '' && v !== null && v !== undefined) body[k] = parseFloat(v);
      }
      await api.put(`/symphonics/assets/${editingAsset.asset.id}/params`, body);
      toast.success('Paramètres actif enregistrés');
      setEditingAsset(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSavingAsset(false);
  };

  const resetAssetParams = async () => {
    if (!editingAsset) return;
    if (!window.confirm('Revenir aux paramètres par défaut du type ?')) return;
    setSavingAsset(true);
    try {
      await api.delete(`/symphonics/assets/${editingAsset.asset.id}/params`);
      toast.success('Paramètres réinitialisés');
      setEditingAsset(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSavingAsset(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
      </div>
    );
  }

  // Un type sans coefficient MECAPA ni taux n'est pas tarifé à zéro : il est en attente
  // de paramétrage. Le serveur le range dans `en_attente_parametrage`, pas dans les
  // revenus nuls, et l'écran doit dire la même chose.
  const typesEnAttente = Object.entries(config?.types || {})
    .filter(([, p]) => p.coeff_mecapa == null || p.taux_mecapa == null)
    .map(([type]) => type);

  return (
    <div className="p-6 md:p-8 lg:p-10 space-y-6" data-testid="symphonics-page">
      <div>
        <Breadcrumb items={[{ label: 'Agrégateurs', to: '/agregateurs' }, { label: 'Estimation Symphonics' }]} />
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">Estimation Symphonics</h1>
          <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200 text-[10px]">Opérateur / Admin</Badge>
        </div>
        <p className="text-sm text-neutral-400 mt-0.5">
          {/* Le PARC, pas la selection Symphonics : la phrase disait « 21 actifs du
              parc dont 21 sous mandat et 2 804 autres agregateurs », soit un total
              plus petit que ses propres parts. Sa version lit le meme champ
              (SymphonicsPage.js:292). */}
          {summary?.total_actifs_operateur ?? summary?.total_actifs_symphonics ?? 0} actifs actifs du parc — dont
          <span className="font-medium text-cyan-700 ml-1">{summary?.ventilation?.symphonics?.actifs ?? 0} sous mandat Symphonics</span>
          {' '}({fmtEUR(summary?.ventilation?.symphonics?.mensuel)}/mois estimé) et {summary?.ventilation?.autres?.actifs ?? 0} autres agrégateurs
        </p>
      </div>

      {/* Widget Estimation Symphonics restauré depuis le Dashboard */}
      <SymphonicsRevenueWidget summary={summary} />

      {/* KPI totaux (mensuel + annuel uniquement) */}
      <div className="grid grid-cols-2 gap-3" data-testid="symphonics-kpis">
        <KPICard label="Estimation mensuelle (total)" value={fmtEUR(summary?.total_mensuel)} accent="text-cyan-700" icon={Euro} tid="kpi-mensuel" />
        <KPICard label="Estimation annuelle (total)" value={fmtEUR(summary?.total_annuel)} accent="text-neutral-900" icon={TrendingUp} tid="kpi-annuel" />
      </div>

      {/* Bandeau contexte : etat parc + part operateur + ventilation famille (aligne Survoltage) */}
      <div className="rounded-lg border border-neutral-200/80 bg-white p-4 flex items-center gap-6 flex-wrap" data-testid="symphonics-context-banner">
        {(() => {
          const byType = summary?.by_type || {};
          const totalMecapa = ['PAC', 'Ballon', 'Batterie', 'PV'].reduce((s, t) => s + Number(byType[t]?.mecapa_an || 0), 0);
          const totalEnergie = ['PAC', 'Ballon', 'Batterie', 'PV'].reduce((s, t) => s + Number(byType[t]?.energie_an || 0), 0);
          const nbSous = summary?.total_actifs_symphonics ?? 0;
          const nbTotal = summary?.total_actifs_operateur ?? nbSous;
          const etatParc = nbTotal > 0 ? Math.round((nbSous / nbTotal) * 100) : 100;
          const partOp = Math.round((Number(summary?.config?.part_operateur ?? 1)) * 100);
          return (
            <>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">État du parc</p>
                <p className="text-lg font-semibold text-neutral-900" data-testid="ctx-etat-parc">{etatParc} %</p>
              </div>
              <Separator orientation="vertical" className="h-8" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">Part opérateur</p>
                <p className="text-lg font-semibold text-emerald-700" data-testid="ctx-part-operateur">{partOp} %</p>
              </div>
              <Separator orientation="vertical" className="h-8" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">Capacitaire estimé (MECAPA)</p>
                <p className="text-sm font-medium text-neutral-800" data-testid="ctx-mecapa">{fmtEUR(totalMecapa)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">Énergie estimée (NEBCO)</p>
                <p className="text-sm font-medium text-neutral-800" data-testid="ctx-energie">{fmtEUR(totalEnergie)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">Équilibrage estimé (MA+R2)</p>
                <p className="text-sm font-medium text-neutral-400" data-testid="ctx-equilibrage">—</p>
              </div>
            </>
          );
        })()}
      </div>

      <MentionEstimation source="symphonics" bloc="symphonics-kpis" />

      {/* Segmentation par type — cartes cliquables filtrant la liste ci-dessous */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-[13px] font-medium text-neutral-900">Ventilation estimée par type d&apos;actif</h2>
          {typeFilter && (
            <button onClick={() => setTypeFilter(null)} className="text-[11px] text-cyan-700 hover:text-cyan-900 font-medium underline" data-testid="clear-type-filter">
              Effacer le filtre ({TYPE_LABEL[typeFilter]})
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {['PAC', 'Ballon', 'Batterie', 'PV'].map(type => {
            const node = summary?.by_type?.[type] || summary?.by_type?.[type === 'Ballon' ? 'ECS' : ''];
            const Icon = TYPE_ICON[type] || HelpCircle;
            const isActive = typeFilter === type;
            const disabled = !node || node.count === 0;
            return (
              <button
                key={type}
                type="button"
                onClick={() => { if (disabled) return; setTypeFilter(prev => prev === type ? null : type); }}
                disabled={disabled}
                className={`text-left p-4 rounded-lg border bg-white transition-all ${
                  isActive
                    ? 'border-cyan-500 ring-2 ring-cyan-100 bg-cyan-50/40 shadow-sm'
                    : disabled
                    ? 'border-neutral-200/80 opacity-60 cursor-not-allowed'
                    : 'border-neutral-200/80 hover:border-cyan-300 hover:shadow-sm cursor-pointer'
                }`}
                data-testid={`symphonics-type-${type}`}
                aria-pressed={isActive}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center ${TYPE_COLOR[type] || 'bg-neutral-50'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-neutral-900">{TYPE_LABEL[type] || type}</p>
                    <p className="text-[10px] text-neutral-400">{node?.count ?? 0} actif{node?.count > 1 ? 's' : ''}</p>
                  </div>
                  {isActive && (
                    <span className="text-[9px] font-semibold text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded-full">Filtre actif</span>
                  )}
                </div>
                {!node || node.count === 0 ? (
                  <p className="text-[11px] text-neutral-400 italic">Aucun actif</p>
                ) : node.en_attente > 0 && node.calcules === 0 ? (
                  <div className="flex items-start gap-1.5 text-[11px] text-amber-600 bg-amber-50 rounded-md p-2" data-testid={`symphonics-en-attente-${type}`}>
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>En attente de paramétrage Symphonics ({node.en_attente} actif{node.en_attente > 1 ? 's' : ''})</span>
                  </div>
                ) : (
                  <div className="space-y-1 text-[11px]">
                    <Row l="Mensuel estimé" v={fmtEUR(node.mensuel)} strong />
                    <Row l="Annuel estimé" v={fmtEUR(node.annuel)} />
                    <Row l="MECAPA/an estimé" v={fmtEUR(node.mecapa_an)} />
                    <Row l="Énergie/an estimée" v={fmtEUR(node.energie_an)} />
                    {node.en_attente > 0 && (
                      <p className="text-[10px] text-amber-600 pt-1">
                        {node.en_attente} actif{node.en_attente > 1 ? 's' : ''} en attente de paramétrage
                      </p>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <MentionEstimation source="symphonics" bloc="symphonics-actifs" className="mt-3" />
      </div>

      {/* Liste détaillée des actifs pris en compte — composant standardisé */}
      {(() => {
        const norm = (t) => (t === 'ECS' ? 'Ballon' : t);
        const filteredList = assetsList
          .filter(a => a.aggregateur === PAGE_AGGREGATEUR && (!typeFilter || norm(a.type) === typeFilter));
        return (
          <AggregatorAssetsTable
            aggregator={PAGE_AGGREGATEUR}
            assets={filteredList}
            accentColor="text-cyan-700"
            badgeClass="bg-cyan-600 hover:bg-cyan-700 text-white"
            filterAggregator={false}
            isAdmin={isAdmin}
            supportsCustomization={true}
            onCustomize={openAssetParams}
            onRefresh={fetchAll}
          />
        );
      })()}

      {/* Configuration Symphonics (edit admin) */}
      <div className="rounded-lg border border-neutral-200/80 bg-white p-5 space-y-4" data-testid="symphonics-config">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-[13px] font-medium text-neutral-900">Paramètres de calcul</h2>
            {/* La formule affichée ici citait `coeff_global`, qui n'existe pas côté
                serveur, et omettait la part opérateur, qui multiplie bien le résultat.
                Elle décrit maintenant ce que `revenu_actif` calcule réellement. */}
            <p className="text-[11px] text-neutral-400">
              <span className="font-mono">MECAPA = Puissance × coeff_MECAPA × coeff_dispo × taux_MECAPA × part_opérateur</span>
            </p>
            <p className="text-[11px] text-neutral-400">
              <span className="font-mono">Énergie = Puissance × taux_énergie × part_opérateur</span>
            </p>
            <p className="text-[10px] text-neutral-400 mt-0.5">
              Les quatre paramètres se règlent par type d&apos;actif ci-dessous, et peuvent être
              surchargés équipement par équipement depuis la liste. Les deux taux ci-contre ne
              servent que de repli quand le type n&apos;en porte pas.
            </p>
          </div>
          {!isAdmin && <Badge variant="outline" className="text-[10px]">Lecture seule</Badge>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ConfigField
            label="Taux MECAPA (repli)"
            suffix="€/kW/an"
            value={form.taux_mecapa}
            onChange={v => setForm(f => ({ ...f, taux_mecapa: v }))}
            disabled={!isAdmin}
            tid="cfg-taux-mecapa"
          />
          <ConfigField
            label="Taux énergie (repli)"
            suffix="€/kW/an"
            value={form.taux_energie}
            onChange={v => setForm(f => ({ ...f, taux_energie: v }))}
            disabled={!isAdmin}
            tid="cfg-taux-energie"
          />
        </div>

        {/* Paramétrage par type d'actif.
            C'est le seul réglage que le serveur applique réellement : les trois champs
            qui vivaient ici (coeff dispo PAC, coeff dispo Ballon, coeff global) étaient
            saisissables, envoyés, et jetés en silence par le modèle serveur qui ne les
            connaît pas. L'écran laissait croire à un réglage qui n'avait aucun effet. */}
        <div className="pt-3 border-t border-neutral-100" data-testid="symphonics-types-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[12px] font-medium text-neutral-800">Paramétrage par type d&apos;actif</p>
            {typesEnAttente.length > 0 && (
              <p className="text-[10.5px] text-amber-700" data-testid="symphonics-types-en-attente">
                En attente de paramétrage : {typesEnAttente.join(', ')}
              </p>
            )}
          </div>
          <div className="space-y-2">
            {Object.keys(config?.types || {}).map(type => (
              <LigneTypeSymphonics
                key={type}
                type={type}
                parametres={config.types[type]}
                disabled={!isAdmin}
                onEnregistrer={enregistrerType}
              />
            ))}
          </div>
        </div>
        {/* Part opérateur — pourcentage négocié avec Symphonics */}
        <div className="pt-3 border-t border-neutral-100" data-testid="cfg-part-operateur-block">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-[12px] font-medium text-neutral-800">Part opérateur</p>
              <p className="text-[10.5px] text-neutral-500">Pourcentage conservé après négociation avec Symphonics (100 % = brut). Appliqué aux estimations mensuelles & annuelles.</p>
            </div>
            <span className="text-lg font-semibold text-emerald-700" data-testid="cfg-part-operateur-value">
              {Math.round((Number(form.part_operateur) || 0) * 100)} %
            </span>
          </div>
          <Slider
            min={0} max={100} step={1}
            value={[Math.round((Number(form.part_operateur) || 0) * 100)]}
            onValueChange={([v]) => setForm(f => ({ ...f, part_operateur: v / 100 }))}
            disabled={!isAdmin}
            data-testid="cfg-part-operateur-slider"
          />
        </div>
        {isAdmin && (
          <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
            <p className="text-[11px] text-neutral-400">
              Laissez un paramètre vide tant que Symphonics n&apos;a pas confirmé sa valeur : le
              type est alors déclaré en attente, et non tarifé à zéro.
            </p>
            <Button size="sm" onClick={saveConfig} disabled={savingConfig} data-testid="save-symphonics-config-btn">
              <Save className="w-3.5 h-3.5 mr-1" /> {savingConfig ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        )}
      </div>

      {/* Versements reels vs estimes */}
      <div className="rounded-lg border border-neutral-200/80 bg-white p-5 space-y-4" data-testid="symphonics-versements">
        <div>
          <h2 className="text-[13px] font-medium text-neutral-900">Versements réels Symphonics</h2>
          <p className="text-[11px] text-neutral-400">Comparaison automatique estimé (au moment de la saisie) vs réel versé</p>
        </div>

        {/* Formulaire ajout */}
        <form onSubmit={addVersement} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end p-3 rounded-md bg-neutral-50/60 border border-neutral-100" data-testid="versement-form">
          <div>
            <Label className="text-[11px] text-neutral-500">Période</Label>
            <Input
              className="h-9"
              placeholder="Ex. Mai 2026 / T2 2026"
              value={versementForm.periode}
              onChange={e => setVersementForm(f => ({ ...f, periode: e.target.value }))}
              data-testid="versement-periode"
            />
          </div>
          <div>
            <Label className="text-[11px] text-neutral-500">Date de versement *</Label>
            <Input
              className="h-9"
              type="date"
              value={versementForm.date_versement}
              onChange={e => setVersementForm(f => ({ ...f, date_versement: e.target.value }))}
              data-testid="versement-date"
              required
            />
          </div>
          <div>
            <Label className="text-[11px] text-neutral-500">Montant réel (€) *</Label>
            <Input
              className="h-9"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={versementForm.montant_reel}
              onChange={e => setVersementForm(f => ({ ...f, montant_reel: e.target.value }))}
              data-testid="versement-montant"
              required
            />
          </div>
          <Button type="submit" size="sm" disabled={savingVersement} data-testid="versement-submit-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
          </Button>
        </form>

        {/* Historique */}
        {versements.length === 0 ? (
          <p className="text-[12px] text-neutral-400 text-center py-6" data-testid="versements-empty">
            Aucun versement enregistré
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Période</TableHead>
                <TableHead>Date versement</TableHead>
                <TableHead className="text-right">Estimé (€)</TableHead>
                <TableHead className="text-right">Réel versé (€)</TableHead>
                <TableHead className="text-right">Écart</TableHead>
                <TableHead className="text-right">Écart %</TableHead>
                {isAdmin && <TableHead className="w-8"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {versements.map(v => {
                const positif = (v.ecart_eur || 0) >= 0;
                return (
                  <TableRow key={v.id} data-testid={`versement-row-${v.id}`}>
                    <TableCell className="font-medium">{v.periode}</TableCell>
                    <TableCell>{fmtDate(v.date_versement)}</TableCell>
                    <TableCell className="text-right text-neutral-500">{fmtEUR(v.estime_a_date)}</TableCell>
                    <TableCell className="text-right font-medium">{fmtEUR(v.montant_reel)}</TableCell>
                    <TableCell className={`text-right font-medium ${positif ? 'text-emerald-600' : 'text-red-600'}`}>
                      <span className="inline-flex items-center gap-0.5">
                        {positif ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {positif ? '+' : ''}{fmtEUR(v.ecart_eur)}
                      </span>
                    </TableCell>
                    <TableCell className={`text-right font-medium ${positif ? 'text-emerald-600' : 'text-red-600'}`}>
                      {positif ? '+' : ''}{Number(v.ecart_pct || 0).toFixed(2)}%
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <button
                          onClick={() => deleteVersement(v.id)}
                          className="p-1 rounded hover:bg-red-50 text-neutral-300 hover:text-red-500"
                          data-testid={`versement-delete-${v.id}`}
                          aria-label="Supprimer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Coefficient de performance PAC (COP global) */}
      <SymphoCOPCard />

      {/* Gestion des documents Symphonics */}
      <AggregatorDocuments aggregator="symphonics" colorClass="text-cyan-600" accentBg="bg-cyan-50" />

      {/* Dialog personnalisation params par actif */}
      <Dialog open={!!editingAsset} onOpenChange={(open) => { if (!open) setEditingAsset(null); }}>
        <DialogContent className="max-w-lg" data-testid="asset-params-dialog">
          <DialogHeader>
            <DialogTitle>Paramètres Symphonics — {editingAsset?.asset?.client_nom}</DialogTitle>
            <DialogDescription>
              {editingAsset?.asset?.type} · Puissance {editingAsset?.asset?.puissance_kw ?? '—'} kW
              {editingAsset?.asset?.type === 'PAC' ? ' (thermique)' : ''}
              <br />
              Laissez un champ vide pour utiliser le défaut du type d&apos;actif.
            </DialogDescription>
          </DialogHeader>
          {editingAsset && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { k: 'coeff_mecapa', label: 'Coeff MECAPA', suffix: '—', step: '0.01' },
                  { k: 'coeff_dispo', label: 'Coeff dispo (global)', suffix: '—', step: '0.01' },
                  { k: 'taux_mecapa', label: 'Taux MECAPA', suffix: '€/kW/an', step: '0.01' },
                  { k: 'taux_energie', label: 'Taux énergie', suffix: '€/kW/an', step: '0.01' },
                ].map(f => {
                  const defaultsMap = editingAsset.asset.revenu?.type_defaults || {};
                  const defVal = defaultsMap[f.k];
                  return (
                    <div key={f.k}>
                      <Label className="text-[11px] text-neutral-500">{f.label}</Label>
                      <div className="relative">
                        <Input
                          className="h-9 pr-14"
                          type="number" step={f.step}
                          value={editingAsset.form[f.k]}
                          placeholder={defVal != null ? `Défaut : ${defVal}` : 'En attente'}
                          onChange={e => setEditingAsset(s => ({ ...s, form: { ...s.form, [f.k]: e.target.value } }))}
                          data-testid={`asset-param-${f.k}`}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400 font-medium">{f.suffix}</span>
                      </div>
                      <p className="text-[9px] text-neutral-400 mt-0.5">Défaut type : {defVal ?? '—'}</p>
                    </div>
                  );
                })}
              </div>
              {editingAsset.asset.symphonics_params && (
                <div className="mt-3 p-2 rounded-md bg-violet-50 border border-violet-100 text-[11px] text-violet-800 flex items-center gap-1">
                  <Sliders className="w-3 h-3" />
                  Cet actif a des paramètres personnalisés actifs.
                </div>
              )}
            </>
          )}
          <DialogFooter className="gap-2">
            {editingAsset?.asset?.symphonics_params && (
              <Button variant="outline" onClick={resetAssetParams} disabled={savingAsset} data-testid="asset-params-reset">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Réinitialiser au défaut
              </Button>
            )}
            <Button variant="outline" onClick={() => setEditingAsset(null)}>Annuler</Button>
            <Button onClick={saveAssetParams} disabled={savingAsset} data-testid="asset-params-save">
              <Save className="w-3.5 h-3.5 mr-1" /> {savingAsset ? '…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPICard({ label, value, sub, accent, icon: Icon, tid }) {
  return (
    <div className="p-4 rounded-lg border border-neutral-200/80 bg-white" data-testid={tid}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5 text-neutral-400" />}
        <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">{label}</p>
      </div>
      <p className={`text-2xl font-semibold tracking-tight ${accent || 'text-neutral-900'}`}>{value}</p>
      {sub && <p className="text-[11px] text-neutral-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function ConfigField({ label, suffix, value, onChange, step = '0.01', disabled, placeholder, highlight, tid }) {
  return (
    <div>
      <Label className="text-[11px] text-neutral-500">{label}</Label>
      <div className="relative">
        <Input
          className={`h-9 pr-14 ${highlight ? 'border-amber-300 bg-amber-50/40' : ''}`}
          type="number"
          step={step}
          value={value}
          placeholder={placeholder || '0'}
          disabled={disabled}
          onChange={e => onChange(e.target.value)}
          data-testid={tid}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400 font-medium">{suffix}</span>
      </div>
      {highlight && (
        <p className="text-[10px] text-amber-600 mt-1">Non renseigné → actifs en attente</p>
      )}
    </div>
  );
}

function Row({ l, v, strong }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-neutral-500">{l}</span>
      <span className={strong ? 'font-semibold text-neutral-900' : 'text-neutral-700 font-medium'}>{v}</span>
    </div>
  );
}

const LIBELLES_TYPE = { Ballon: 'Ballon ECS' };
const CHAMPS_TYPE = [
  { cle: 'coeff_mecapa', label: 'Coeff. MECAPA' },
  { cle: 'coeff_dispo', label: 'Coeff. dispo' },
  { cle: 'taux_mecapa', label: 'Taux MECAPA' },
  { cle: 'taux_energie', label: 'Taux énergie' },
];

/**
 * Les quatre paramètres Symphonics d'un type d'actif, enregistrables d'un bloc.
 *
 * L'enregistrement est par type et non par champ : le serveur réécrit les quatre champs
 * du type à chaque appel, donc un enregistrement champ par champ effacerait les autres.
 */
function LigneTypeSymphonics({ type, parametres, disabled, onEnregistrer }) {
  const valeursServeur = useMemo(
    () => Object.fromEntries(CHAMPS_TYPE.map(({ cle }) => [cle, parametres?.[cle] ?? ''])),
    [parametres],
  );
  const [valeurs, setValeurs] = useState(valeursServeur);
  const [enCours, setEnCours] = useState(false);

  // Après un enregistrement, la config rechargée devient la nouvelle référence.
  useEffect(() => { setValeurs(valeursServeur); }, [valeursServeur]);

  const modifie = CHAMPS_TYPE.some(({ cle }) => String(valeurs[cle]) !== String(valeursServeur[cle]));
  const enAttente = parametres?.coeff_mecapa == null || parametres?.taux_mecapa == null;

  const enregistrer = async () => {
    setEnCours(true);
    try {
      await onEnregistrer(type, valeurs);
      toast.success(`Paramètres ${LIBELLES_TYPE[type] || type} enregistrés`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
      setValeurs(valeursServeur);
    }
    setEnCours(false);
  };

  return (
    <div
      className={`grid grid-cols-2 md:grid-cols-6 gap-2 items-end rounded-md border p-2 ${enAttente ? 'border-amber-200 bg-amber-50/30' : 'border-neutral-100 bg-neutral-50/40'}`}
      data-testid={`symph-type-${type}`}
    >
      <div className="md:col-span-1">
        <p className="text-[12px] font-medium text-neutral-800">{LIBELLES_TYPE[type] || type}</p>
        {enAttente && (
          <p className="text-[10px] text-amber-700">En attente</p>
        )}
      </div>
      {CHAMPS_TYPE.map(({ cle, label }) => (
        <div key={cle}>
          <Label className="text-[10px] text-neutral-500">{label}</Label>
          <Input
            className="h-8 text-[11px] bg-white"
            type="number"
            step="0.01"
            value={valeurs[cle]}
            placeholder="—"
            disabled={disabled}
            onChange={(e) => setValeurs((v) => ({ ...v, [cle]: e.target.value }))}
            data-testid={`symph-${type}-${cle.replace('_', '-')}`}
          />
        </div>
      ))}
      <div>
        {!disabled && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 w-full text-[11px]"
            onClick={enregistrer}
            disabled={enCours || !modifie}
            data-testid={`symph-${type}-enregistrer`}
          >
            {enCours ? '…' : 'Enregistrer'}
          </Button>
        )}
      </div>
    </div>
  );
}
